import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { LockKeyhole, Pencil } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import Select from 'app/frontend/base/components/Select'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { displayErrorToast } from 'app/frontend/base/helpers'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { updateEventParticipantSignup } from 'app/frontend/base/api/event'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useApplyEventUpdate from '../hooks/useApplyEventUpdate'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import ViewerParticipantActivitySummary from './ViewerParticipantActivitySummary'
import {
  getSelectableRolesForCharacter,
  getViewerSelectableCharacters,
  SignupRoleSelect,
  SignupStatusSelect,
  viewerSignupCharacterSelectOptions,
} from './viewerSignupShared'
import { toast } from 'sonner'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'

type Props = {
  event: wowaudit.Event
  entries: EventActivityParticipantEntry[]
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
}

const ExistingParticipantSection = ({
  entry,
  activeActivities,
  linkedPlanDataByActivityId,
  selectableStatuses,
  isSaving,
  isLocked,
  selectableCharacters,
  onSave,
}: {
  entry: EventActivityParticipantEntry
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  isSaving: boolean
  isLocked: boolean
  selectableCharacters: wowaudit.CharacterReference[]
  onSave: (
    participantId: number,
    status: wowaudit.EventParticipantReference['status'],
    comment: string,
    characterId?: number,
    role?: CharacterRole,
  ) => void
}) => {
  const { t } = useTranslation()
  const character = resolveEventParticipantCharacter(entry)
  const [draftStatus, setDraftStatus] = useState(entry.participant.status)
  const [draftComment, setDraftComment] = useState(
    entry.participant.comment || '',
  )
  const [draftCharacterId, setDraftCharacterId] = useState(
    entry.participant.characterId.toString(),
  )
  const [draftRole, setDraftRole] = useState(
    entry.participant.role as CharacterRole,
  )
  const [editorOpen, setEditorOpen] = useState(false)
  const classColoursForCharacter = classColours[character.characterClass]
  const selectedCharacter = selectableCharacters.find(
    (selectableCharacter) =>
      selectableCharacter.id.toString() === draftCharacterId,
  )
  const selectableRoles = useMemo(() => {
    return getSelectableRolesForCharacter(selectedCharacter?.classId)
  }, [selectedCharacter?.classId])
  const isDirty =
    draftStatus !== entry.participant.status ||
    draftComment !== (entry.participant.comment || '') ||
    draftCharacterId !== entry.participant.characterId.toString() ||
    draftRole !== entry.participant.role
  const canChangeCharacter =
    entry.participant.addedAsGuest && selectableCharacters.length > 0

  return (
    <div className='flex gap-3 max-sm:flex-col'>
      <Card className='space-y-2 min-w-34'>
        <div
          className={cn(
            'flex items-center justify-center gap-1.5',
            classColoursForCharacter.text,
          )}
        >
          <CharacterClassIcon characterClass={character.characterClass} />
          <span>{character.name}</span>
        </div>

        <div className='flex flex-col gap-2 max-sm:flex-row max-sm:items-center'>
          <Badge
            variant='outline'
            className='flex items-center justify-center gap-2 max-sm:flex-1'
          >
            <EventStatusIcon status={entry.participant.status} size='small' />
            <span className='text-sm'>
              {t(
                `settings.raidsAvailableStatuses.options.${entry.participant.status}`,
              )}
            </span>
          </Badge>

          {isLocked ? (
            <Badge
              variant='warning'
              className='h-6 px-2 gap-1.5 justify-center w-full max-sm:flex-1'
            >
              <LockKeyhole className='w-3.5 h-3.5' />
              Locked
            </Badge>
          ) : (
            <Popover
              open={editorOpen}
              onOpenChange={(open) => {
                setEditorOpen(open)
                if (!open) return
                setDraftStatus(entry.participant.status)
                setDraftComment(entry.participant.comment || '')
                setDraftCharacterId(entry.participant.characterId.toString())
                setDraftRole(entry.participant.role as CharacterRole)
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  disabled={isSaving}
                  size='custom'
                  className='w-full flex items-center justify-center gap-2 py-0.5 max-sm:flex-1'
                >
                  <Pencil className='w-3.5 h-3.5' />
                  Edit
                </Button>
              </PopoverTrigger>
              <PopoverContent className='p-3 flex flex-col gap-2' align='end'>
                {canChangeCharacter ? (
                  <Select
                    value={draftCharacterId}
                    setValue={(value) => {
                      if (!value) return
                      setDraftCharacterId(value)
                      const nextCharacter = selectableCharacters.find(
                        (selectableCharacter) =>
                          selectableCharacter.id.toString() === value,
                      )
                      if (!nextCharacter) return
                      const nextRoles = getSelectableRolesForCharacter(
                        nextCharacter.classId,
                      )
                      if (!nextRoles.length) return
                      if (nextRoles.includes(draftRole)) return
                      if (
                        nextCharacter.role &&
                        nextRoles.includes(nextCharacter.role)
                      ) {
                        setDraftRole(nextCharacter.role)
                        return
                      }
                      setDraftRole(nextRoles[0])
                    }}
                    options={viewerSignupCharacterSelectOptions(
                      selectableCharacters,
                    )}
                    disabled={isSaving}
                    optionWidth='fit'
                  />
                ) : null}
                {canChangeCharacter ? (
                  <SignupRoleSelect
                    value={draftRole}
                    setValue={setDraftRole}
                    roles={selectableRoles}
                    disabled={isSaving || selectableRoles.length === 0}
                  />
                ) : null}
                <SignupStatusSelect
                  statuses={selectableStatuses}
                  value={draftStatus}
                  setValue={setDraftStatus}
                  disabled={isSaving}
                />
                <Input
                  value={draftComment}
                  disabled={isSaving}
                  onChange={(event) => setDraftComment(event.target.value)}
                  placeholder='Comment'
                />
                <Button
                  size='sm'
                  className='w-full'
                  disabled={!isDirty || isSaving}
                  onClick={() => {
                    onSave(
                      entry.participant.id,
                      draftStatus,
                      draftComment,
                      canChangeCharacter ? Number(draftCharacterId) : undefined,
                      canChangeCharacter ? draftRole : undefined,
                    )
                    setEditorOpen(false)
                  }}
                >
                  Save
                </Button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </Card>

      <div className='self-stretch grow'>
        <ViewerParticipantActivitySummary
          entry={entry}
          activeActivities={activeActivities}
          linkedPlanDataByActivityId={linkedPlanDataByActivityId}
        />
      </div>
    </div>
  )
}

const ViewerExistingParticipantsCard = ({
  entries,
  event,
  activeActivities,
  linkedPlanDataByActivityId,
  selectableStatuses,
}: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const applyEventUpdate = useApplyEventUpdate()
  const [savingParticipantId, setSavingParticipantId] = useState<number>()
  const signupMutation = useMutation({
    mutationFn: async ({
      participantId,
      status,
      comment,
      characterId,
      role,
    }: {
      participantId: number
      status: wowaudit.EventParticipantReference['status']
      comment: string
      characterId?: number
      role?: CharacterRole
    }) => {
      return await updateEventParticipantSignup(team.id, participantId, {
        status,
        comment,
        characterId,
        role,
      })
    },
    onSuccess: (updatedEvent) => {
      applyEventUpdate(updatedEvent)
      setSavingParticipantId(undefined)
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => {
      setSavingParticipantId(undefined)
      displayErrorToast(error as Error, t)
    },
  })

  const saveExistingParticipant = (
    participantId: number,
    status: wowaudit.EventParticipantReference['status'],
    comment: string,
    characterId?: number,
    role?: CharacterRole,
  ) => {
    setSavingParticipantId(participantId)
    signupMutation.mutate({ participantId, status, comment, characterId, role })
  }
  const { selectableCharacters } = getViewerSelectableCharacters({
    event,
    team,
    user,
  })

  return (
    <Card className='mb-3 pt-2 w-fit'>
      <h3 className='mt-0 mb-2 text-base font-semibold'>
        {t('dashboard.team.events.viewer-signup.title')}
      </h3>

      {entries.map((entry, index) => (
        <div key={entry.participant.id} className='space-y-3'>
          {index > 0 ? <Separator className='mt-3' /> : null}
          <ExistingParticipantSection
            entry={entry}
            activeActivities={activeActivities}
            linkedPlanDataByActivityId={linkedPlanDataByActivityId}
            selectableStatuses={selectableStatuses}
            isSaving={savingParticipantId === entry.participant.id}
            isLocked={event.status === 'locked'}
            selectableCharacters={selectableCharacters}
            onSave={saveExistingParticipant}
          />
        </div>
      ))}
    </Card>
  )
}

export default ViewerExistingParticipantsCard
